import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, Trash2, TruckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const Transport = () => {
  const { t } = useLanguage();
  const [transports, setTransports] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    country: '',
    cost_per_kg: 0,
    fixed_fee: 0
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [transportRes, reportRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport-costs`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport-monthly-report`, { withCredentials: true })
      ]);
      setTransports(transportRes.data);
      setMonthlyReport(reportRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cost_per_kg: parseFloat(formData.cost_per_kg),
        fixed_fee: parseFloat(formData.fixed_fee)
      };
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/transport-costs`, payload, { withCredentials: true });
      toast.success('Coût de transport ajouté');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating transport cost:', error);
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (transportId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce coût de transport ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/transport-costs/${transportId}`, { withCredentials: true });
      toast.success('Coût supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting transport cost:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ country: '', cost_per_kg: 0, fixed_fee: 0 });
  };

  const totalDelivered = monthlyReport.reduce((sum, m) => sum + m.delivered_count, 0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Coûts de transport</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion des coûts par pays</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-transport-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Ajouter un pays
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau coût de transport</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Pays*</Label>
                  <Input data-testid="transport-country-input" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} required placeholder="France" />
                </div>
                <div className="space-y-2">
                  <Label>Coût par kg*</Label>
                  <Input data-testid="transport-cost-input" type="number" step="0.01" min="0" value={formData.cost_per_kg} onChange={(e) => setFormData({ ...formData, cost_per_kg: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Frais fixe par commande</Label>
                  <Input data-testid="transport-fixed-fee-input" type="number" step="0.01" min="0" value={formData.fixed_fee} onChange={(e) => setFormData({ ...formData, fixed_fee: e.target.value })} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-transport-button" type="submit" className="bg-slate-900 hover:bg-slate-800">Créer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Liste des coûts de transport</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : transports.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun coût de transport configuré</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Pays</TableHead>
                    <TableHead className="font-medium text-right">Coût par kg</TableHead>
                    <TableHead className="font-medium text-right">Frais fixe</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transports.map((transport) => (
                    <TableRow key={transport.id} data-testid="transport-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{transport.country}</TableCell>
                      <TableCell className="text-right font-medium">{transport.cost_per_kg.toFixed(2)} &euro;</TableCell>
                      <TableCell className="text-right">{(transport.fixed_fee || 0).toFixed(2)} &euro;</TableCell>
                      <TableCell>
                        <Button data-testid="delete-transport-button" size="sm" variant="ghost" onClick={() => handleDelete(transport.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* COMPTE RENDU MENSUEL - LIVRAISONS */}
        <Card data-testid="transport-monthly-report-card" className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TruckIcon size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Compte rendu mensuel - Livraisons</CardTitle>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Nombre de commandes livrées par mois &mdash; Total : {totalDelivered} commande(s) livrée(s)
            </p>
          </CardHeader>
          <CardContent>
            {monthlyReport.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucune commande livrée</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Mois</TableHead>
                    <TableHead className="font-medium text-right">Commandes livrées</TableHead>
                    <TableHead className="font-medium text-right">Poids total (kg)</TableHead>
                    <TableHead className="font-medium text-right">Frais de transport (EUR / VND)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyReport.map((m) => (
                    <TableRow key={m.month_label} data-testid="transport-monthly-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{m.month_label}</TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center justify-center bg-emerald-100 text-emerald-800 font-bold rounded-full px-3 py-1 text-sm">
                          {m.delivered_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">{m.total_weight.toFixed(2)} kg</TableCell>
                      <TableCell data-testid="transport-monthly-cost" className="text-right">
                        <div className="font-semibold text-slate-900">{(m.transport_cost || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} &euro;</div>
                        <div className="text-xs text-slate-500">{(m.transport_cost_vnd || 0).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
